import {useEffect, useMemo, useState} from 'react';

import {searchForAll} from 'src/openapi/admin/apis/EntitlementsPresetSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {AccountPreset} from './useAccountPreset';

export function useAllPresets() {
    const apiDispatch = useAppDispatch();
    const [presets, setPresets] = useState<Array<AccountPreset>>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);
        setError(null);

        apiDispatch(searchForAll())
            .then(response => {
                const payload = response.payload;
                if (payload.success) {
                    setPresets(payload.data.map(preset => {
                        return {
                            presetId: preset.id || '',
                            presetName: preset.name || '',
                            presetDescription: preset.description || '',
                        };
                    }));
                } else {
                    setError('Failed to load presets');
                }
                setLoading(false);
            })
            .catch(err => {
                setError(typeof err === 'string' ? err : 'Unknown error');
                setLoading(false);
            });
    }, [apiDispatch]);

    const filteredPresets = useMemo(() => {
        const safeSearch = (searchTerm || '').toLowerCase();
        return presets.filter(preset =>
            preset.presetName.toLowerCase().includes(safeSearch.toLowerCase()),
        );
    }, [presets, searchTerm]);

    const getConferenceLicensePreset = (): AccountPreset | null => {
        return presets.find(preset => preset.presetName === 'Conference License') || null;
    };

    const getPresetByLicenseType = (licenseType: string): AccountPreset | null => {
        const licenseTypeToPresetName: Record<string, string> = {
            UN: 'Unlimited License',
            CU: 'Concurrent Users License',
            SB: 'Seat Based License',
            TR: 'Trial License',
            CN: 'Conference License',
            PF: 'Partner Freemium License',
            RC: 'Research Center License',
            RT: 'Research Center Trial License',
            US: 'University (Student) License',
            IR: 'Investor Relations License',
            GP: 'Grace Period License',
            UGP: 'University Grace Period License',
        };

        const presetName = licenseTypeToPresetName[licenseType];
        if (!presetName) {
            return null;
        }

        return presets.find(preset => preset.presetName === presetName) || null;
    };

    return {
        presets,
        filteredPresets,
        loading,
        error,
        searchTerm,
        setSearchTerm,
        getConferenceLicensePreset,
        getPresetByLicenseType,
    };
}
